import React, { useState, useEffect, useRef, useMemo } from 'react';
import { RgbStringColorPicker } from 'react-colorful';
import tinycolor from 'tinycolor2';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import RgbDisplay from 'components/shared/RgbDisplay/';
import Button from 'components/shared/Button';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

function MaskTools() {
  const dispatch = useAppDispatch();
  const borderColor = useAppSelector(activeEditor.selectors.borderColor);

  const [showColorPicker, setShowColorPicker] = useState(false);

  const restoreColor = useRef<string>('FFFFFFFF');

  useEffect(() => {
    function handleClose({ target }: MouseEvent) {
      const picker = document.querySelector('.mask-tools .color-picker');
      if (picker && !picker.contains(target as HTMLElement)) {
        setShowColorPicker(false);
      }
    }

    if (showColorPicker) {
      document.addEventListener('click', handleClose);
    } else {
      document.removeEventListener('click', handleClose);
    }

    return function cleanup() {
      document.removeEventListener('click', handleClose);
    };
  }, [showColorPicker]);

  function handleColorChange(color: string) {
    const parsed = tinycolor(color);
    if (parsed.isValid()) {
      dispatch(canvases.actions.updateCanvas({ borderColor: parsed.toHex8() }));
    }
  }

  // Set it back to the previously used value
  function handleColorCancel() {
    setShowColorPicker(false);
    dispatch(canvases.actions.updateCanvas({ borderColor: restoreColor.current }));
  }

  // It's already been set in redux state, so update our 'restore'
  function handleColorChoose() {
    if (borderColor) {
      restoreColor.current = borderColor;
    }

    setShowColorPicker(false);
  }

  const rgbBorderColor = useMemo(() => tinycolor(borderColor).toRgbString(), [borderColor]);

  return (
    <div className="mask-tools">
      {showColorPicker && (
        <div className="color-picker">
          <RgbStringColorPicker color={rgbBorderColor} onChange={handleColorChange} />
          <div className="color-picker__controls">
            <input
              type="text"
              value={rgbBorderColor}
              onChange={(e) => handleColorChange(e.target.value)}
            />
            <Button
              type="bare"
              className="color-picker-button cancel-button"
              onClick={handleColorCancel}
            >
              cancel
            </Button>
            <Button
              type="bare"
              className="color-picker-button choose-button"
              onClick={handleColorChoose}
            >
              choose
            </Button>
          </div>
        </div>
      )}
      <Button
        type="bare"
        className="sp-preview icon border-color-button"
        onClick={() => setShowColorPicker(!showColorPicker)}
      >
        <span className="screen-reader-text">Change border color</span>
      </Button>
      <RgbDisplay color={borderColor} />
    </div>
  );
}

export default MaskTools;
